import { api } from 'boot/axios'

/**
 * @typedef {{
 *   identifier: string,
 *   finess: string,
 *   udiDiCode: ?string,
 *   name: string,
 *   commercialName: ?string,
 *   clinicalStudy: boolean,
 *   branding: ?string,
 *   theme: ?Object,
 *   customOptions: ?Object,
 *   anonymousPatients: boolean,
 *   creationDate: string,
 *   updateDate: ?string
 * }} EstablishmentData
 */

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   gamCode: ?string,
 *   establishmentId: string,
 *   pathologyId: ?string,
 *   externalIdentifiers: EstablishmentServiceExternalIdentifierData[],
 *   pathology?: PathologyData,
 *   prescriptionsVisibleForPatients: ?boolean
 *   prescriptionsGenerationEnabled: boolean
 *   conversationDefaultStatus: string
 * }} EstablishmentServiceData
 */

/**
 * @typedef {{
 *   id: string,
 *   value: string,
 *   system: string,
 *   creationDate: string,
 *   updateDate: ?string,
 * }} EstablishmentServiceExternalIdentifierData
 */

/**
 * @typedef {{
 *   page?: int,
 *   pageSize?: int,
 *   establishmentId?: ?string,
 *   finess?: ?string,
 *   personPrivileges?: ?string[],
 *   withEstablishment?: boolean,
 *   withPathology?: boolean,
 *   withCurrentComplianceSettings?: boolean
 * }} ListEstablishmentServicesParams
 *
 * "withCurrentComplianceSettings" doesn't do anything without "withPathology"
 *
 * @typedef {{
 *   identifier: string,
 *   firstName: string,
 *   lastName: string,
 *   name: string,
 *   type: 'professional'
 * }} User
 *
 * @typedef {{
 *   id: string,
 *   title: string,
 *   message: string,
 *   endDate: string,
 *   cancellationDate: ?string,
 *   cancelledBy: ?User
 * }} BroadcastMessage
 *
 * @typedef {{
 *   title: string,
 *   message: string,
 *   endDate: string,
 *   service: string
 * }} BroadcastMessageCreate
 *
 * @typedef {{
 *   title: string,
 *   message: string,
 *   endDate: string
 * }} BroadcastMessageUpdate
 */

/**
 * @return {Promise<import("axios").AxiosResponse<{totalItems: int, items: EstablishmentData[]}>>}
 */
export function listEstablishments() {
  return api.get('/establishments', {
    params: {
      normalizedOutput: true
    }
  })
}

export function updateEstablishment(establishment) {
  return api.put(`/establishments/${establishment.identifier}`, establishment)
}

export function createEstablishment(establishment) {
  return api.post('/establishments', establishment)
}

/**
 * @param {ListEstablishmentServicesParams} params
 * @return {Promise<{
 *   data: {totalItems: int, items: EstablishmentServiceData[]}
 * }>}
 */
export function listEstablishmentServices(params) {
  return api.get('/establishment_services', { params })
}

export function updatePersonLinkWithEstablishmentService(identifier, personIdentifier, privileges) {
  if (!privileges) {
    privileges = []
  }
  return api.put(`/establishment_services/${identifier}/members/${personIdentifier}`, { privileges })
}

export function removePersonFromEstablishmentService(identifier, personIdentifier) {
  return api.delete(`/establishment_services/${identifier}/members/${personIdentifier}`)
}

export function updateEstablishmentService(id, establishmentService) {
  return api.put(`/establishment_services/${id}`, establishmentService)
}

export function createEstablishmentService(establishmentService) {
  return api.post('/establishment_services', establishmentService)
}

export function addInterventionLevelUser(interventionLevelId, identifier, establishmentService) {
  return api.post(`/persons/${identifier}/intervention_levels/${interventionLevelId}`, { establishmentService })
}

export function removeInterventionLevelUser(interventionLevelId, identifier, establishmentService) {
  return api.delete(`/persons/${identifier}/intervention_levels/${interventionLevelId}`, { data: { establishmentService } })
}

export function listIdentityDocumentTypes(establishmentId) {
  return api.get(`/establishments/${establishmentId}/identity_document_types`)
}

export function createIdentityDocumentType(establishmentId, identityDocumentType) {
  return api.post(`/establishments/${establishmentId}/identity_document_types`, identityDocumentType)
}

export function updateIdentityDocumentType(establishmentId, identityDocumentType) {
  return api.put(`/establishments/${establishmentId}/identity_document_types/${identityDocumentType.id}`, identityDocumentType)
}

export function deleteIdentityDocumentType(establishmentId, identityDocumentTypeId) {
  return api.delete(`/establishments/${establishmentId}/identity_document_types/${identityDocumentTypeId}`)
}

export function getClients() {
  return api.get('/establishments/clients')
}

export function getTheme(establishmentId) {
  return api.get(`/establishments/${establishmentId}/theme`)
}

export function getStructuresHierarchy() {
  return api.get('/establishments/structures_hierarchy')
}
